import { useLive } from './live';
import { useThreadPanel } from './thread';

/** What is on screen: a channel (maybe with a thread open), the "Dallar" page, or the list. */
interface View {
  channelId: string | null;
  rootId: string | null;
  threads: boolean;
  /** How many views back the app's own history goes (0: the first one). */
  depth: number;
}

const current = (depth: number): View => ({
  channelId: useLive.getState().selectedChannelId,
  rootId: useThreadPanel.getState().open?.rootId ?? null,
  threads: useThreadPanel.getState().page,
  depth,
});
const same = (a: View, b: View) => a.channelId === b.channelId && a.rootId === b.rootId && a.threads === b.threads;
const viewOf = (state: unknown): View | null => (state as { nc?: View } | null)?.nc ?? null;

let started = false;
let applying = false;

/**
 * Each change of view becomes a browser history entry, so the app's back buttons, the browser's
 * and the mouse's all go to the previous view instead of always to the list.
 */
export function startNavigation() {
  if (started) return;
  started = true;
  let last = current(0);
  history.replaceState({ ...history.state, nc: last }, '');
  let scheduled = false;
  // Opening a thread changes the channel and the panel one after the other: one entry, after both.
  const record = () => {
    if (applying || scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      // Closing a thread is no step of its own: back from the channel then goes where the channel came from.
      const closing = last.rootId !== null && useThreadPanel.getState().open === null && useLive.getState().selectedChannelId === last.channelId;
      const next = current(closing ? last.depth : last.depth + 1);
      if (same(next, last)) return;
      last = next;
      if (closing) history.replaceState({ nc: next }, '');
      else history.pushState({ nc: next }, '');
    });
  };
  useLive.subscribe(record);
  useThreadPanel.subscribe(record);
  window.addEventListener('popstate', (event) => {
    const view = viewOf(event.state) ?? { channelId: null, rootId: null, threads: false, depth: 0 };
    applying = true;
    useLive.getState().select(view.channelId);
    useThreadPanel.setState({ open: view.channelId && view.rootId ? { channelId: view.channelId, rootId: view.rootId } : null, page: view.threads && !view.channelId });
    applying = false;
    last = view;
  });
}

/** The app's back button: the previous view, or the list when there is none. */
export function goBack() {
  if ((viewOf(history.state)?.depth ?? 0) > 0) return history.back();
  useLive.getState().select(null);
  useThreadPanel.setState({ open: null, page: false });
}
